import { useEffect, useRef, useState } from "react";

const navLinks = [
  { href: "#hero", label: "今日热搜" },
  { href: "#feed", label: "热点速递" },
  { href: "#deep", label: "深度盘点" },
  { href: "#trend", label: "吃瓜排行" },
  { href: "#faq", label: "常见问答" },
];

type NavbarProps = {
  onSearch: (keyword: string) => void;
  totalHot: number;
};

export default function Navbar({ onSearch, totalHot }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(keyword.trim());
    setOpen(false);
    document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-[0_1px_0_oklch(var(--background-200))]"
          : "bg-background-50/60 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        {/* logo */}
        <a href="#hero" className="group flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-xl shadow-sm">
            <i className="ri-restaurant-line text-background-50"></i>
          </span>
          <span className="font-display text-2xl leading-none text-foreground-950">
            色瓜网
          </span>
          <span className="hidden rounded-full bg-secondary-500 px-2 py-0.5 text-xs font-medium text-background-50 sm:inline-block">
            今日吃瓜速递
          </span>
        </a>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* search desktop */}
          <form
            onSubmit={handleSubmit}
            className="hidden items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-1.5 md:flex"
            role="search"
          >
            <i className="ri-search-line text-sm text-foreground-400"></i>
            <input
              ref={searchRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索新瓜关键词…"
              className="w-36 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400 lg:w-44"
              aria-label="搜索关键词"
            />
          </form>

          <a
            href="#feed"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            <i className="ri-fire-fill text-base"></i>
            今日 {totalHot} 条新瓜
          </a>

          {/* mobile toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-900 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={open}
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <form onSubmit={handleSubmit} className="flex items-center gap-2 px-4 py-3 md:hidden" role="search">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索新瓜关键词…"
              className="flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              aria-label="搜索关键词"
            />
          </form>
          <nav className="flex flex-col px-2 pb-4" aria-label="移动端导航">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-sm text-foreground-800 hover:bg-background-100"
              >
                {l.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}